import axios, { AxiosInstance } from 'axios';
import { recordRumError, recordRumEvent } from 'src/analytics/rum';
import {
  API_COUNT,
  API_ERROR,
  API_LATENCY,
  API_SUCCESS,
  UNKNOWN_API_NAME,
  UNKNOWN_COMPONENT,
} from 'src/constants/MonitoringConstants';

let gtcServiceClient: ApiClient;

export function getGtcHubClientInstance(): ApiClient {
  if (!gtcServiceClient) {
    const taaEnablementStageConfig = `${window.location.origin}/api`;
    gtcServiceClient = new ApiClient(taaEnablementStageConfig);
  }

  return gtcServiceClient;
}

export interface MetaData {
  apiName?: string;
  component?: string;
  startTime?: number;
}

declare module 'axios' {
  interface AxiosRequestConfig {
    meta?: MetaData | undefined;
  }
}

export class ApiClient {
  readonly client: AxiosInstance;

  constructor(apiBaseUrl: string) {
    this.client = axios.create({
      baseURL: apiBaseUrl,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
      responseType: 'json',
    });

    this.client.interceptors.request.use((request) => {
      request.meta = request.meta ?? {};
      request.meta.startTime = Date.now();

      const apiName = request.meta.apiName ?? UNKNOWN_API_NAME;
      const component = request.meta.component ?? UNKNOWN_COMPONENT;

      [`${apiName}_${component}`, apiName].forEach((element) => {
        recordRumEvent(API_COUNT, { element, apiName, component });
      });

      return request;
    });

    this.client.interceptors.response.use(
      (response) => {
        const meta: MetaData = response.config.meta ?? {};
        const apiName = meta.apiName ?? UNKNOWN_API_NAME;
        const component = meta.component ?? UNKNOWN_COMPONENT;
        const latency =
          meta.startTime === undefined
            ? undefined
            : Date.now() - meta.startTime;

        [`${apiName}_${component}`, apiName].forEach((element) => {
          recordRumEvent(API_SUCCESS, {
            element,
            apiName,
            component,
            statusCode: response.status,
          });
          if (latency !== undefined) {
            recordRumEvent(API_LATENCY, {
              element,
              apiName,
              component,
              value: latency,
            });
          }
        });

        return response.data;
      },
      (error) => {
        const meta: MetaData = error.config?.meta ?? {};
        const apiName = meta.apiName ?? UNKNOWN_API_NAME;
        const component = meta.component ?? UNKNOWN_COMPONENT;
        const statusCode = error.response?.status;
        const latency =
          meta.startTime === undefined
            ? undefined
            : Date.now() - meta.startTime;

        [`${apiName}_${component}`, apiName].forEach((element) => {
          recordRumEvent(API_ERROR, {
            element,
            apiName,
            component,
            statusCode,
            url: error.config?.url,
            method: error.config?.method,
            message: error.message,
          });
          if (latency !== undefined) {
            recordRumEvent(API_LATENCY, {
              element,
              apiName,
              component,
              value: latency,
            });
          }
        });

        recordRumError(error);

        return Promise.reject(error);
      },
    );
  }
}
export {};
